const T = require("react").PropTypes;
const moment = require("moment");

const Error = require("./Error.js").PropType;
const Page = require("./Page.js").PropType;
const WalkThru = require("./WalkThru.js").PropType;
const QuestionValidationStatus = require("./QuestionValidationStatus.js");
const Question = require("./Question.js").PropType;

function byDisplayOrder(a, b) {
    if (a.DisplayOrder < b.DisplayOrder) {
        return -1;
    }

    if (a.DisplayOrder > b.DisplayOrder) {
        return 1;
    }

    return 0;
}

const Survey = {
    /* For now, we only include props that we're actually using */
    PropType: T.shape({
        GUID: T.string,
        Type: T.string,
        FacilityNumber: T.string,
        AuditPeriod: T.string,
        Pages: T.arrayOf(Page),
        IsReadOnly: T.bool,
        CurrentPageGUID: T.string,
        CurrentSectionGUID: T.string, 
        IsLoading: T.bool,
        LoadError: Error,
        Attachments: T.arrayOf(T.shape({
            AuditID: T.string,
            Name: T.string,
            Key: T.string
        })),
        CurrentQuestionSnapshot: T.arrayOf(Question),
        // Fields for summaries only
        ConfirmationNumber: T.string,
        CompletedOn: T.object, // Date
        CompletedBy: T.string,
        MostRecentAudit: T.object, // Date
        Score: T.number,
        MaxScore: T.number,
        SampleScore: T.number,
        SampleMaxScore: T.number, 
        IsSaving: T.bool,
        SaveError: Error
    }),
    FromViewChange(changedToSurveyEvent, original) {
        if (!original
            || original.Type !== changedToSurveyEvent.SurveyType
            || original.GUID !== changedToSurveyEvent.SurveyID
            || original.Pages === null) {
            const freshSurvey = Survey.FreshFromViewChange(changedToSurveyEvent);

            return freshSurvey;
        }

        const updatedSurvey =
            Survey.CurrentFromViewChange(changedToSurveyEvent, original);

        return updatedSurvey;
    },
    FreshFromViewChange(changedToSurveyEvent) {
        return {
            GUID: changedToSurveyEvent.SurveyID,
            Type: changedToSurveyEvent.SurveyType,
            FacilityNumber: changedToSurveyEvent.FacilityNumber,
            AuditPeriod: "Unspecified",
            Pages: null,
            IsReadOnly: false,
            CurrentPageGUID: changedToSurveyEvent.PageID,
            CurrentSectionGUID: changedToSurveyEvent.SectionID,
            IsLoading: false,
            LoadError: null,
            // There is no current section, so there is no question snapshot.
            CurrentQuestionSnapshot: []
        };
    },
    ForReload(surveySpecEvent) {
        return {
            GUID: surveySpecEvent.SurveyID,
            Type: surveySpecEvent.SurveyType,
            FacilityNumber: surveySpecEvent.FacilityNumber,
            AuditPeriod: "Unspecified",
            Pages: null,
            IsReadOnly: false,
            CurrentPageGUID: null,
            CurrentSectionGUID: null,
            IsLoading: false,
            LoadError: null,
            // There is no current section, so there is no question snapshot.
            CurrentQuestionSnapshot: []
        };
    },
    CurrentFromViewChange(changedToSurveyEvent, original) {
        // Rare case: we don't have any pages.  If this happens,
        // we still want to keep the specified page and section
        // IDs in state, even though we know they won't be valid,
        // just in case pages get loaded later.
        if ((original.Pages || []).length === 0) {
            const asIsReplacement = Object.assign({}, original, {
                CurrentPageGUID: changedToSurveyEvent.PageID,
                CurrentSectionGUID: changedToSurveyEvent.SectionID
            });

            return asIsReplacement;
        }

        const page = findFirstValidPage(
            changedToSurveyEvent.PageID,
            original.Pages);

        const section = findFirstValidSection(
            changedToSurveyEvent.SectionID,
            page.Sections);

        const update = {
            CurrentPageGUID: page.GUID,
            CurrentSectionGUID: section.GUID,
            // Because the section changed, we need to update the question
            // snapshot also.
            CurrentQuestionSnapshot: JSON.parse(JSON.stringify(section.Questions))
        };

        const replacement = Object.assign({}, original, update);

        return replacement;
    },
    FromLoadingEvent(original) {
        const update = Object.assign({}, original, {
            IsLoading: true,
            LoadError: null
        });

        return update;
    },
    FromLoadedEvent(original, loadedData) {
        const pages = loadedData.Pages.sort(byDisplayOrder)
            .map(p => {
                p.Sections = p.Sections.sort(byDisplayOrder);

                p = QuestionValidationStatus.PageWithValidation(p);

                return p;
            });

        const currentPage = findFirstValidPage(original.CurrentPageGUID, pages);
        const currentSection =
            findFirstValidSection(original.CurrentSectionGUID, currentPage.Sections);

        const updated = Object.assign({}, original, {
            Pages: pages,
            AuditPeriod: loadedData.AuditPeriod,
            CurrentPageGUID: currentPage.GUID,
            CurrentSectionGUID: currentSection.GUID,
            IsLoading: false,
            LoadError: null,
            // Because we now have a section, we need to take a snapshot of its questions
            // for dirty-checking.
            CurrentQuestionSnapshot: JSON.parse(JSON.stringify(currentSection.Questions))
        });

        return updated;
    },
    FromLoadedLegacySurveyEvent(original, loadedData) {
        const updated = Object.assign({}, original, {
            Pages: null,
            AuditPeriod: loadedData.AuditPeriod,
            CurrentPageGUID: null,
            CurrentSectionGUID: null,
            IsLoading: false,
            LoadError: null,
            CurrentQuestionSnapshot: null,
            // Special summary fields
            ConfirmationCode: loadedData.ConfirmationCode,
            CompletedOn: moment(loadedData.CompletedOn).toDate(),
            CompletedBy: loadedData.CompletedBy,
            MostRecentAudit: moment(loadedData.MostRecentAuditDate).toDate(),
            Score: loadedData.Score,
            MaxScore: loadedData.MaxScore,
            Attachments: loadedData.Attachments
        });

        return updated;
    },
    FromSampleScore(original, score, maxScore) { 
        const updated = Object.assign({}, original, {
            SampleScore: score,
            SampleMaxScore: maxScore
        });

        return updated; 
    }, 
    FromLoadedSummaryEvent(original, loadedData) {
        const pages = loadedData.Audit.Pages.sort(byDisplayOrder)
            .map(p => {
                p.Sections = p.Sections.sort(byDisplayOrder);

                return p;
            });

        const updated = Object.assign({}, original, {
            Pages: pages,
            AuditPeriod: loadedData.Audit.AuditPeriod,
            CurrentPageGUID: null,
            CurrentSectionGUID: null,
            IsLoading: false,
            LoadError: null,
            CurrentQuestionSnapshot: null,
            // Special summary fields
            ConfirmationCode: loadedData.ConfirmationCode,
            CompletedOn: moment(loadedData.Audit.CompletedTime).toDate(), // ???
            CompletedBy: loadedData.Audit.CompletedBy, // ???
            MostRecentAudit: moment(loadedData.MostRecentAuditDate).toDate(),
            Score: loadedData.Audit.Score,
            MaxScore: loadedData.Audit.MaxScore,
            Attachments: loadedData.Attachments
        });

        return updated;
    },
    FromErrorEvent(original, error) {
        const updated = Object.assign({}, original, {
            Pages: null,
            IsReadOnly: false,
            IsLoading: false,
            LoadError: error
        });

        return updated;
    }
};

function findFirstValidPage(specifiedPageID, withinPages) {
    const page = withinPages.find(p => p.GUID === specifiedPageID);

    if (!page) {
        return withinPages[0];
    }

    return page;
}

function findFirstValidSection(specifiedSectionID, withinSections) {
    const section = withinSections.find(s => s.GUID === specifiedSectionID);

    if (!section) {
        return withinSections[0];
    }

    return section;
}

module.exports = Survey;


// WEBPACK FOOTER //
// ./src/types/Survey.js